<template>
    <!--
      Navigation Bar Container
      Handles the layout and responsive behavior of the navbar.
    -->
    <div class="navbar-container position-relative">
        <div class="d-flex flex-row align-items-center navbar-content">
            <!--
              Page Title
              Visible only on desktop (hidden on mobile devices).
            -->
            <h3 class="col m-0 text-start d-none d-md-block">
                <template v-if="'header' in $route.meta">{{
                    $route.meta.header
                }}</template>
            </h3>

            <!--
              Mobile Left Section
              Contains the mobile menu toggle button, visible only on mobile devices.
            -->
            <div class="col d-flex d-md-none p-0">
                <button
                    class="icon-btn icon-btn-white icon-btn-lg"
                    @click="toggleMobileMenu"
                    aria-label="Menu"
                >
                    <i class="fi fi-hamburger"></i>
                </button>
            </div>

            <!--
              Search Bar
              Visible only on desktop (hidden on mobile devices).   Also hidden on prod
            -->
            <!--<SearchBar class="col d-none d-md-block search-bar" v-model="searchTerm" />-->

            <!--
              Mobile Right Group
              Contains search, notifications, chat, and avatar buttons, visible only on mobile devices.
            -->
            <div
                class="col d-flex d-md-none justify-content-end p-0"
                style="gap: 1rem"
            >
                <!-- Search Icon (Toggle search bar) -->

                <!---
                <button
                    class="icon-btn icon-btn-white icon-btn-lg"
                    @click="toggleSearch"
                    aria-label="Search"
                >
                    <i class="fi fi-search"></i>
                </button>
                -->

                <router-link
                    v-if="$userStore.isViewOnly && $userStore.isCompanyAdmin"
                    class="btn btn-secondary btn-upgrade"
                    type="button"
                    aria-label="Upgrade"
                    :to="billingRoute"
                >
                    {{ upgradeText }}
                </router-link>

                <!-- Patty Button -->
                <button
                    class="icon-btn icon-btn-white icon-btn-lg position-relative"
                    type="button"
                    aria-label="Patty"
                    @click="pattyStore.openNew()"
                >
                    <i class="fi fi-patty"></i>
                </button>

                <!-- Chat Button -->
                <button
                    class="icon-btn icon-btn-white icon-btn-lg position-relative"
                    type="button"
                    aria-label="Chat"
                    @click="messengerStore.openHistory()"
                >
                    <span
                        v-if="notificationStore.hasUnreadMessage"
                        class="notification-dot"
                    ></span>
                    <i class="fi fi-thread"></i>
                </button>

                <!-- Notification Button -->
                <button
                    class="icon-btn icon-btn-white icon-btn-lg position-relative"
                    type="button"
                    aria-label="Notifications"
                    @click="notificationStore.openPanel()"
                >
                    <span
                        v-if="notificationStore.hasUnread"
                        class="notification-dot"
                    ></span>
                    <i class="fi fi-bell"></i>
                </button>

                <!-- User Avatar -->
                <div class="d-flex align-items-center" style="gap: 0.5rem">
                    <UserAvatar :user="user" clickable />
                </div>
            </div>

            <!--
              Desktop Right Group
              Contains chat, notifications, and user information, visible only on desktop devices.
            -->
            <div
                class="d-none d-md-flex col justify-content-end align-items-center p-0"
                style="gap: 2.5rem"
            >
                <div class="d-flex" style="gap: 1rem">
                    <router-link
                        v-if="$userStore.isViewOnly && $userStore.isCompanyAdmin"
                        class="btn btn-secondary btn-upgrade"
                        type="button"
                        aria-label="Upgrade"
                        :to="billingRoute"
                    >
                        {{ upgradeText }}
                    </router-link>
                    <!-- Patty Button -->
                    <button
                        class="icon-btn icon-btn-white position-relative"
                        type="button"
                        aria-label="Patty"
                        @click="pattyStore.openNew()"
                    >
                        <i class="fi fi-patty"></i>
                    </button>
                    <!-- Chat Button -->
                    <button
                        class="icon-btn icon-btn-white position-relative"
                        type="button"
                        aria-label="Chat"
                        @click="messengerStore.openHistory()"
                    >
                        <span
                            v-if="notificationStore.hasUnreadMessage"
                            class="notification-dot"
                        ></span>
                        <i class="fi fi-thread"></i>
                    </button>

                    <!-- Notifications Button -->
                    <button
                        class="icon-btn icon-btn-white position-relative"
                        type="button"
                        aria-label="Notifications"
                        @click="notificationStore.openPanel()"
                    >
                        <span
                            v-if="notificationStore.hasUnread"
                            class="notification-dot"
                        ></span>
                        <i class="fi fi-bell"></i>
                    </button>
                </div>

                <!-- User Information -->
                <div
                    class="d-flex flex-row align-items-center justify-content-between"
                    style="gap: 0.75rem"
                >
                    <UserAvatar :user="user" clickable />
                    <!-- Display user name if available -->
                    <span v-if="userName" class="text-capitalize user-name">{{
                        userName
                    }}</span>
                </div>
            </div>

            <!--
              Separator Line
              Visually separates the navbar content from the rest of the page.
            -->
            <div class="navbar-bottom-line" role="separator"></div>
        </div>

        <!--
          Mobile Search Bar
          Visible only when `showMobileSearch` is true and on mobile devices.
        -->
        <div
            v-if="showMobileSearch"
            class="col fixed-top w-100 d-md-none mobile-search-container d-flex flex-column"
        >
            <div class="row justify-content-end m-0">
                <!-- Close Searchbar Button -->
                <button
                    @click="toggleSearch"
                    class="icon-btn icon-btn-white"
                    type="button"
                    aria-label="Close Searchbar"
                >
                    <i class="fi fi-x"></i>
                </button>
            </div>
            <!-- SearchBar Component with Reference for Focusing -->
            <SearchBar />
        </div>
    </div>
</template>

<script>
/**
 * AppNavbar.vue
 *
 * The navigation bar component responsible for displaying the page title,
 * search bar, notification icons, chat icons, and user avatar.
 * It handles both desktop and mobile views with responsive design.
 */

import SearchBar from '@/components/fields/SearchBar.vue';
import UserAvatar from '@/components/UserAvatar.vue';

import { useMessengerStore } from '@/stores/messenger';
import { useNotificationStore } from '@/stores/notification';
import { usePattyStore } from '@/stores/patty';

export default {
    name: 'AppNavbar',
    components: {
        SearchBar,
        UserAvatar,
    },
    props: {
        /**
         * Controls the visibility of the sidebar.
         * Passed down from the parent component.
         * @type {Boolean}
         * @default true
         */
        showSidebar: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['open-sidebar', 'close-sidebar', 'open-notification'],
    data() {
        return {
            /**
             * Indicates if user data is being loaded.
             * @type {Boolean}
             */
            isLoading: true,

            /**
             * Stores the user object fetched from the store.
             * @type {Object|null}
             */
            user: null,

            /**
             * Stores the user's full name.
             * @type {String|null}
             */
            userName: null,

            /**
             * Controls the visibility of the mobile search bar.
             * @type {Boolean}
             */
            showMobileSearch: false,

            /**
             * Current string in search bar
             * @type {String|null}
             */
            searchTerm: '',

            messengerStore: null,
            notificationStore: null,
            pattyStore: null,

            billingRoute: {
                name: 'Settings',
                query: { tab: 'billing' },
                hash: '#manage-plan',
            },
        };
    },
    computed: {
        upgradeText() {
            return this.$windowWidth < 941 ? 'Upgrade' : 'Upgrade Today!';
        },
    },
    methods: {
        /**
         * Toggles the visibility of the mobile sidebar menu.
         * Emits the 'toggle-mobile-menu' event to inform the parent component.
         */
        toggleMobileMenu() {
            if (this.showSidebar) this.$emit('close-sidebar');
            else this.$emit('open-sidebar');
        },

        /**
         * Toggles the visibility of the mobile search bar.
         * If the search bar is shown, it focuses the input field after rendering.
         */
        toggleSearch() {
            this.showMobileSearch = !this.showMobileSearch;

            // Once mobile search is visible, focus the input
            this.$nextTick(() => {
                if (this.showMobileSearch && this.$refs.searchBarRef) {
                    this.$refs.searchBarRef.focusSearchInput();
                }
            });
        },

        /**
         * Sets the user information from the user store.
         * Constructs the user's full name and handles loading state.
         */
        setUser() {
            this.user = this.$userStore.user;
            if (this.user) {
                // Concatenate first and last name, trim whitespace
                this.userName =
                    `${this.user?.first_name ?? ''} ${
                        this.user?.last_name ?? ''
                    }`.trim() || null;
            }
            this.isLoading = false;
        },

        /**
         * Fetches the user data by syncing with the user store.
         * Calls `setUser` upon successful synchronization.
         */
        fetchUser() {
            this.$userStore.sync(() => this.setUser());
        },
    },
    mounted() {
        // Fetch user data when the component is mounted
        this.fetchUser();
    },
    created() {
        this.messengerStore = useMessengerStore();
        this.notificationStore = useNotificationStore();
        this.pattyStore = usePattyStore();
    },
};
</script>

<style scoped>
/*
      Styles for the AppNavbar component.
      Ensures proper layout, responsiveness, and visual aesthetics.
    */

.navbar-container {
    height: 68px;
    padding: 0;
    margin: 0;
    margin-inline: var(--space-10);
    transition: all 0.3s ease-in-out;
    overflow-y: none;
}

.navbar-content {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    position: relative;
    padding: var(--size-3) 0;
    overflow-x: none;
}

.search-bar {
    max-width: 432px;
    min-width: 144px;
}

.navbar-bottom-line {
    border-bottom: 1px solid var(--neutral-600);
    position: absolute;
    bottom: 0;
    width: 100%;
}

.notification-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    background-color: var(--danger);
    border: 2px solid var(--white);
    border-radius: 50%;
}

.user-name {
    font-size: 14px;
    line-height: 1.57;
    color: var(--neutral-900);
    max-width: 6.25rem;

    /* Multi-line truncation */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;

    /* Ensure height matches 2 lines */
    max-height: calc(2 * 1.57em); /* 2 lines * line-height */
    white-space: normal;
}

.mobile-search-container {
    background-color: var(--white);
    height: auto;
    padding: 1rem;
    gap: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
    transition: all 0.3s ease-in-out;
    transform-origin: top;
}

.btn-upgrade {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    min-width: 0;
}

@media only screen and (max-width: 1200px) {
    .navbar-container {
        margin-inline: var(--space-4);
    }
}

/* Mobile View Styles */
@media only screen and (max-width: 767.98px) {
    .navbar-container {
        height: 60px;
    }

    .navbar-content {
        width: 100%;
    }
}
</style>
